import { formatPrice } from "./utility";

const RECEIPT_WIDTH = 48;

export const ESC = {
  INIT: "\x1B\x40",
  BOLD_ON: "\x1B\x45\x01",
  BOLD_OFF: "\x1B\x45\x00",
  CENTER: "\x1B\x61\x01",
  LEFT: "\x1B\x61\x00",
  CUT: "\x1D\x56\x00",
  CODEPAGE_CP850: "\x1B\x74\x02",
};

const normalizeText = (text = "") =>
  String(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .replace(/€/g, "EUR");

const line = () => "-".repeat(RECEIPT_WIDTH) + "\n";
const doubleLine = () => "=".repeat(RECEIPT_WIDTH) + "\n";

const center = (text) => {
  const safe = normalizeText(text);
  const pad = Math.max(0, Math.floor((RECEIPT_WIDTH - safe.length) / 2));
  return " ".repeat(pad) + safe + "\n";
};

const padRight = (text, len) =>
  (normalizeText(text) + " ".repeat(len)).substring(0, len);

const padLeft = (text, len) =>
  (" ".repeat(len) + normalizeText(text)).slice(-len);

const wrapText = (text, width) => {
  const safe = normalizeText(text || "-");
  const chunks = [];
  for (let i = 0; i < safe.length; i += width) {
    chunks.push(safe.slice(i, i + width));
  }
  return chunks.length ? chunks : ["-"];
};

const getModeReglementLabel = (mode) => {
  switch (mode) {
    case "ESPECES":
      return "Especes";
    case "CARTE_BANCAIRE":
      return "Carte bancaire";
    case "CHEQUE":
      return "Cheque";
    case "VIREMENT":
      return "Virement";
    case "TRAITE":
      return "Traite";
    default:
      return mode || "-";
  }
};

const getReceiptPaymentSummary = (data) => {
  const total = Number(data?.montantTotalTTC || 0);
  const cash = Number(data?.montantPayeCash ?? data?.montantPayeEspece ?? 0);
  const card = Number(data?.montantPayeCarte || 0);
  const stampDuty = Number(data?.montantDroitTimbre || 0);
  const totalDue = total + stampDuty;
  const explicitPaid = Number(data?.montantPaye || 0);
  const computedPaid = cash + card;
  const paid = explicitPaid > 0 ? Math.max(explicitPaid, computedPaid) : computedPaid;
  const change = Math.max(paid - totalDue, 0);
  return { cash, card, paid, change, stampDuty, totalDue };
};

const amountRow = (label, amount, bold = false) => {
  let row = "";
  if (bold) row += ESC.BOLD_ON;
  row += padRight(label, 30) + padLeft(formatPrice(amount || 0, false), 18) + "\n";
  if (bold) row += ESC.BOLD_OFF;
  return row;
};

const formatClientSolde = (totalDebit, totalCredit) => {
  const debit = Number(totalDebit || 0);
  const credit = Number(totalCredit || 0);
  const solde = credit - debit;
  const sign = solde > 0 ? "+" : solde < 0 ? "-" : "";
  return `${sign}${formatPrice(Math.abs(solde), false)}`;
};

export const buildEscPosReceipt = ({
  businessName,
  address,
  telephone,
  receiptData,
}) => {
  const payment = getReceiptPaymentSummary(receiptData);
  const totalQuantity = (receiptData?.lignes || []).reduce(
    (sum, item) => sum + Number(item.quantiteLivree || 0),
    0
  );

  let t = "";
  t += ESC.INIT;
  t += ESC.CODEPAGE_CP850;

  t += ESC.CENTER + ESC.BOLD_ON;
  t += center(businessName || "MATJARPOS");
  t += ESC.BOLD_OFF;

  if (address) {
    normalizeText(address)
      .split("\n")
      .forEach((part) => {
        t += center(part);
      });
  }

  if (telephone) {
    t += center(`Tel : ${telephone}`);
  }

  t += doubleLine();
  t += ESC.CENTER + ESC.BOLD_ON;
  t += center("TICKET DE CAISSE");
  t += ESC.BOLD_OFF + ESC.LEFT;

  t += `${padRight("Ticket", 12)}: ${normalizeText(receiptData?.numero || "-")}\n`;
  t += `${padRight("Date", 12)}: ${normalizeText(
    receiptData?.createdAt || receiptData?.dateOperation || "-"
  )}\n`;
  t += `${padRight("Client", 12)}: ${normalizeText(receiptData?.clientNom || "Passager")}\n`;
  if (receiptData?.createdBy) {
    t += `${padRight("Caissier", 12)}: ${normalizeText(receiptData.createdBy)}\n`;
  }

  t += line();
  t += ESC.BOLD_ON;
  t +=
    padRight("DESIGNATION", 28) +
    padLeft("QTE", 5) +
    padLeft("P.U.", 7) +
    padLeft("TOTAL", 8) +
    "\n";
  t += ESC.BOLD_OFF + line();

  (receiptData?.lignes || []).forEach((item) => {
    const nameLines = wrapText(item.articleNom || "-", 28);
    const qte = String(Number(item.quantiteLivree || 0));
    const pu = formatPrice(item.prixUnitaireTTC || 0, false);
    const total = formatPrice(item.montantTTC || 0, false);

    t +=
      padRight(nameLines[0], 28) +
      padLeft(qte, 5) +
      padLeft(pu, 7) +
      padLeft(total, 8) +
      "\n";

    nameLines.slice(1).forEach((part) => {
      t += padRight(`  ${part}`, RECEIPT_WIDTH) + "\n";
    });
  });

  t += line();
  t +=
    padRight(`${receiptData?.lignes?.length || 0} article(s)`, 30) +
    padLeft(`${totalQuantity} piece(s)`, 18) +
    "\n";
  t += line();

  t += amountRow("TOTAL TTC", receiptData?.montantTotalTTC || 0, true);
  if (payment.stampDuty > 0) {
    t += amountRow("Droit timbre 0,25%", payment.stampDuty);
    t += doubleLine();
    t += amountRow("NET A PAYER", payment.totalDue, true);
  }
  t += doubleLine();

  t += ESC.BOLD_ON + "PAIEMENT\n" + ESC.BOLD_OFF;
  t += `${padRight("Mode", 30)}${padLeft(getModeReglementLabel(receiptData?.modeReglement), 18)}\n`;
  t += amountRow("Especes", payment.cash);
  t += amountRow("Carte", payment.card);
  t += amountRow("Montant paye", payment.paid);
  if (payment.change > 0) {
    t += amountRow("Monnaie", payment.change, true);
  }

  if (
    !receiptData?.isComptoir &&
    receiptData?.clientTotalDebit &&
    receiptData?.clientTotalCredit
  ) {
    t += line();
    t += ESC.BOLD_ON + padRight("SOLDE CLIENT", RECEIPT_WIDTH) + "\n" + ESC.BOLD_OFF;
    t += padLeft(formatClientSolde(receiptData.clientTotalDebit, receiptData.clientTotalCredit), RECEIPT_WIDTH) + "\n";
  }

  t += line();
  t += "\n";
  t += ESC.CENTER;
  t += "Merci de votre confiance\n";
  t += center("A bientot");
  t += "cima.ma\n\n";
  t += ESC.CUT;

  return t;
};
